import { Card, Icons, Stack, Typography } from '@healthinal/ui';
import type { ReactNode } from 'react';
import { formatDateWithGermanMonth } from '../../utils/date.ts';
import { FieldChangeComparison } from './FieldChangeComparison.tsx';
import type { ChangeHistoryEntryData, HistoryAction, HistoryActor, HistoryFieldChange } from './historyTypes.ts';

function formatHistoryActorName(actor?: HistoryActor) {
  return [actor?.firstName, actor?.lastName].filter(Boolean).join(' ');
}

function getHistoryActionIcon(action: HistoryAction) {
  switch (action) {
    case 'created':
      return <Icons.AddCircleOutline color={'success'} />;
    case 'deleted':
      return <Icons.Delete color={'error'} />;
    case 'updated':
      return <Icons.ChangeCircleOutlined color={'primary'} />;
  }
}

export function ChangeHistoryEntry({
  entry,
  subject,
  section,
  entityName,
  actionLabel,
  changedBy = formatHistoryActorName(entry.changedBy),
}: {
  entry: ChangeHistoryEntryData;
  subject: ReactNode;
  section?: ReactNode;
  entityName: ReactNode;
  actionLabel: ReactNode;
  changedBy?: ReactNode;
}) {
  const showChanges = entry.action === 'updated' && entry.changes && entry.changes.length > 0;

  return (
    <Stack gap={1} paddingBottom={2}>
      <Stack direction={'row'} gap={0.5} alignItems={'center'} flexWrap={'wrap'}>
        {getHistoryActionIcon(entry.action)}
        <Typography level={'title-lg'}>{formatDateWithGermanMonth(entry.date)}</Typography>
        <Typography level={'body-lg'}>{subject}</Typography>
        {section && <Typography level={'body-lg'}>({section})</Typography>}
        <Typography level={'title-lg'}>{entityName}</Typography>
        <Typography level={'title-lg'}>{actionLabel}</Typography>
        <Typography level={'body-lg'}>{changedBy}</Typography>
      </Stack>
      {showChanges && (
        <Card>
          <FieldChangeComparison changes={entry.changes as HistoryFieldChange[]} />
        </Card>
      )}
    </Stack>
  );
}
